

import {
    StepperDropdownContainer,
    StepperDropdownContent,
    StepperDropdownHeader,
    StepperDropdownHeaderIcon,
    StepperDropdownHeaderTitle
} from '../../dropdown/StepperDropdown'
import { CheckBoxSvg, SortSvg, StepCompletedSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { FC, useMemo, useState } from 'react'
import { StepperProps } from '../../../utils/stepper-types'
import Select from '../../../../../CommonComponents/select/Select'
import useStaticData from '../../../hooks/useStaticData'
import useForm from '../../../hooks/useForm'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { selectUnselectAllGs, selectUnselectGs } from '../../../utils/viewer-packets'
import useViewer from '../../../hooks/useViewer'
import useScenarioResponse from '../../../hooks/useScenarioResponse'
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../../constants'

const GroundSegments: FC<StepperProps> = ({ title, hideComponent, open, disabled, stepCompleted, onToggle }) => {
    const { viewer } = useViewer()
    const { allStations } = useStaticData()
    const { formData, setFormData } = useForm()
    const { scenarioResponse } = useScenarioResponse()

    const [sortFilter, setSortFilter] = useState<'AtoZ' | 'ZtoA'>('AtoZ')

    const filterStations = useMemo(() => {
        if (sortFilter === 'AtoZ') {
            return allStations.sort((a, b) => a.groundStationName.localeCompare(b.groundStationName))
        } else {
            return allStations.sort((a, b) => b.groundStationName.localeCompare(a.groundStationName))
        }
    }, [allStations, sortFilter])

    const selectedStations = useMemo(() => formData.segments.stations, [formData.segments.stations])
    const contactType = useMemo(() => formData.segments.contactType, [formData.segments.contactType])

    const scenarioDeploymentType = useMemo(() => scenarioResponse?.scenarioDeploymentType || SCENARIO_DEPLOYMENT_TYPES.SIMULATE, [scenarioResponse?.scenarioDeploymentType])
    const openScenario = useMemo(() => scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER, [scenarioDeploymentType])
    const selectComponentValue = useMemo(() => selectedStations?.length === 0 ? null : selectedStations?.length === filterStations?.length ? 'All' : `${selectedStations?.length} Selected`, [selectedStations, filterStations])
    const selectedStation = useMemo(() => selectComponentValue ? selectComponentValue === 'All' ? `All (${filterStations?.length}) Selected` : selectComponentValue : undefined, [selectComponentValue, filterStations])

    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.nativeEvent.stopImmediatePropagation()
    }

    const onStationSelect = (stationId: string | null) => {
        if (!stationId) return
        if (stationId === 'All') {
            setFormData((prev) => {
                const data = {
                    ...prev,
                    segments: {
                        ...prev.segments,
                        stations: prev?.segments?.stations?.length === filterStations?.length ? [] : filterStations?.map(s => s?.groundStationId)
                    }
                }
                selectUnselectAllGs(viewer, prev?.segments?.stations?.length === filterStations?.length ? false : true)
                return data
            })

        } else {
            setFormData((prev) => {
                const data = {
                    ...prev,
                    segments: {
                        ...prev.segments,
                        stations: prev.segments.stations.includes(stationId) ? prev.segments.stations.filter(s => s !== stationId) : [...prev.segments.stations, stationId]
                    }
                }
                selectUnselectGs(viewer, stationId, data.segments.stations.includes(stationId) ? 'checked' : 'un_checked')
                return data
            })
        }
    }

    if (hideComponent) return null
    return (
        <StepperDropdownContainer
            open={open}
            disabled={disabled}
        >
            <StepperDropdownHeader
                title={title}
                onToggle={onToggle}
            >
                <StepperDropdownHeaderTitle>
                    {title}
                </StepperDropdownHeaderTitle>
                <StepperDropdownHeaderIcon show={stepCompleted}>
                    <StepCompletedSvg />
                </StepperDropdownHeaderIcon>
            </StepperDropdownHeader>
            <StepperDropdownContent>
                <div className='form__input__container'>
                    <span className='form__input__label'>Contact</span>
                    <div className='form__field__container'>
                        <div className='form__toggle__button__container'>
                            {['Continuous', 'Pass-Based'].map((item) => {
                                const active = contactType === item ? 'active' : ''
                                const disabled = !openScenario ? item === 'Pass-Based' : item === 'Continuous'
                                return (
                                    <button disabled={disabled} key={item} className={`form__toggle__button ${active}`}>{item}</button>
                                )
                            })}
                        </div>
                    </div>
                </div>

                <div className={`form__input__container ${filterStations?.length === 0 ? 'disabled' : ''}`}>
                    <span className='form__input__label'>Ground Station</span>
                    <div className={`form__field__container`}>
                        {filterStations?.length === 0 && <span className='form__input__placeholder'>No Station Available</span>}
                        {filterStations?.length > 0 &&
                            <Select id={1} placement="bottom"
                                style={{ width: '140px' }}
                                value={selectComponentValue}
                                onSelect={(value) => { onStationSelect(value) }}
                                multiselect
                            >
                                <CustomToolTip title={selectedStation || ''} placement='bottom-start'>
                                    <Select.Trigger disabled={filterStations?.length === 0}>
                                        <Select.Value placeholder="Select Station" value={selectedStation} />
                                    </Select.Trigger>
                                </CustomToolTip>
                                <Select.Content>
                                    <Select.Group>
                                        <Select.Item value='All'>
                                            <div className='d-flex gap-2 align-items-center w-100'>
                                                <CheckBoxSvg checked={filterStations?.length === selectedStations?.length ? 'checked' : selectedStations?.length > 0 ? 'indeterminate' : 'unchecked'} />
                                                <span className='w-90'>All</span>
                                            </div>
                                        </Select.Item>
                                        {filterStations.map((station) => {
                                            const checked = selectedStations?.includes(station.groundStationId) ? 'checked' : 'unchecked'
                                            return (
                                                <CustomToolTip key={station.groundStationId} title={station.groundStationName} placement="right">
                                                    <Select.Item key={station.groundStationId} value={station.groundStationId}>
                                                        <div className='d-flex gap-2 align-items-center w-100'>
                                                            <CheckBoxSvg checked={checked} />
                                                            <span className='w-90 text-truncate'>{station.groundStationName}</span>
                                                        </div>
                                                    </Select.Item>
                                                </CustomToolTip>
                                            )
                                        })}
                                    </Select.Group>
                                </Select.Content>
                            </Select>
                        }
                        <CustomToolTip title='Sort Ground Station' placement='top'>
                            <button className='filter__button'
                                onMouseDown={stopPropagation}
                                onClick={(e) => {
                                    stopPropagation(e)
                                    setSortFilter(sortFilter === 'ZtoA' ? 'AtoZ' : 'ZtoA')
                                }}
                            >
                                <SortSvg type={sortFilter} />
                            </button>
                        </CustomToolTip>
                    </div>
                </div>

                <div className='form__input__container disabled'>
                    <span className='form__input__label'>Telemetry Collection</span>
                    <div className='form__field__container'>
                        <Select id={2} placement="bottom" value={'Public'} style={{ width: '140px' }}>
                            <Select.Trigger disabled>
                                <Select.Value placeholder="Select Operation" />
                            </Select.Trigger>
                            <Select.Content>
                                <Select.Group>
                                    <Select.Item value='Public'>Public</Select.Item>
                                </Select.Group>
                            </Select.Content>
                        </Select>
                    </div>
                </div>
            </StepperDropdownContent>
        </StepperDropdownContainer>
    )
}

export default GroundSegments